import { useCallback, useEffect, useState } from 'react';
import { selectTenantPrompt } from '../../copy';
import {
  Alert, Button, Card, Empty, Modal, Select, Space, Tag, Typography, Popconfirm, message,
} from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { space } from '@viq-admin/ui';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { useAbility } from '../../ability/AbilityContext';
import { QuestionForm } from '../../resources/questions/QuestionForm';
import {
  listConferenceQuestions, importInventory, deleteConferenceQuestion,
  reorderConferenceQuestions, createConferenceQuestion, updateConferenceQuestion,
  listInventoryQuestions,
} from '../../api/discovery';
import type {
  ConferenceDiscoveryQuestion, DiscoveryQuestion, QuestionPayload,
} from '../../api/discoveryTypes';

function summary(q: ConferenceDiscoveryQuestion): string {
  if (q.type === 'free_text') return 'Free-text';
  const opts = q.options.map((o) => o.text);
  const head = opts.length <= 4 ? opts.join(' · ') : `${opts.length} options`;
  return `Choice · ${q.allowMultiple ? 'multi' : 'single'} · ${head}`;
}

/**
 * One question row with move-up / move-down / edit / remove controls.
 *
 * NOTE (portal delta): the customer portal reorders via `@dnd-kit` drag-and-drop.
 * That package isn't a declared dependency of `apps/console`, so the console
 * uses explicit ↑/↓ buttons — same result (a reordered id list), no phantom dep.
 */
function QuestionCard({
  q, index, count, onMove, onEdit, onRemove, canManage,
}: {
  q: ConferenceDiscoveryQuestion;
  index: number;
  count: number;
  onMove: (dir: -1 | 1) => void;
  onEdit: () => void;
  onRemove: () => void;
  canManage: boolean;
}) {
  return (
    <Card style={{ marginBottom: space.sm }} styles={{ body: { padding: space.md } }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space>
          {canManage && (
            <Space.Compact direction="vertical" size="small">
              <Button
                type="text" size="small" aria-label="Move question up"
                icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onMove(-1)}
              />
              <Button
                type="text" size="small" aria-label="Move question down"
                icon={<ArrowDownOutlined />} disabled={index === count - 1} onClick={() => onMove(1)}
              />
            </Space.Compact>
          )}
          <div>
            <div>
              <b>{q.text}</b> {q.required && <Typography.Text type="danger">*</Typography.Text>}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{summary(q)}</Typography.Text>
          </div>
        </Space>
        <Space>
          {q.required && <Tag color="gold">Required</Tag>}
          {canManage && <Button size="small" onClick={onEdit}>Edit</Button>}
          {canManage && (
            <Popconfirm
              title="Remove this question from the conference?"
              description={q.responseCount > 0 ? `${q.responseCount} captured answer${q.responseCount === 1 ? '' : 's'} will also be permanently deleted.` : undefined}
              onConfirm={onRemove}
            >
              <Button size="small" danger>Remove</Button>
            </Popconfirm>
          )}
        </Space>
      </Space>
    </Card>
  );
}

/**
 * Conference Discovery tab. Per-(company, conference): the target tenant comes
 * from the global "Operate as tenant" scope (top-right `CompanyScopePicker`).
 * No tenant scoped → a prompt (the backend 422s without `?companyId=`).
 */
export function ConferenceDiscovery({ conferenceId }: { conferenceId: string }) {
  const { companyId } = useCompanyScope();
  const hasScope = companyId != null;
  const canManage = useAbility().can('manage', 'DiscoveryQuestion');

  const [questions, setQuestions] = useState<ConferenceDiscoveryQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [startedBlank, setStartedBlank] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [inventory, setInventory] = useState<DiscoveryQuestion[]>([]);
  const [picked, setPicked] = useState<string[]>([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ConferenceDiscoveryQuestion | null>(null);
  const [savedToInventory, setSavedToInventory] = useState(false);

  const refresh = useCallback(async () => {
    if (companyId == null) { setQuestions([]); return; }
    setLoading(true);
    try {
      setQuestions(await listConferenceQuestions(conferenceId, companyId));
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [conferenceId, companyId]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function seedDefaults() {
    if (companyId == null) return;
    try {
      const imported = await importInventory(conferenceId, { defaults: true }, companyId);
      if (imported.length === 0) {
        message.info('No Inventory defaults to copy — set them up in the Questions Inventory first.');
        return;
      }
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Seed failed');
    }
  }

  async function openAddModal() {
    if (companyId == null) return;
    setPicked([]);
    setAddOpen(true);
    try {
      setInventory(await listInventoryQuestions(companyId));
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to load inventory');
    }
  }

  async function confirmAdd() {
    if (companyId == null) return;
    if (picked.length === 0) { setAddOpen(false); return; }
    try {
      await importInventory(conferenceId, { inventoryIds: picked }, companyId);
      setAddOpen(false);
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Add failed');
    }
  }

  async function onMove(index: number, dir: -1 | 1) {
    if (companyId == null) return;
    const to = index + dir;
    if (to < 0 || to >= questions.length) return;
    const next = [...questions];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved);
    setQuestions(next);
    try {
      await reorderConferenceQuestions(conferenceId, next.map((q) => q.id), companyId);
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Reorder failed');
      await refresh();
    }
  }

  async function onRemove(id: string) {
    if (companyId == null) return;
    try {
      await deleteConferenceQuestion(conferenceId, id, companyId);
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Remove failed');
    }
  }

  async function onSubmitEditor(payload: QuestionPayload) {
    if (companyId == null) return;
    try {
      if (editing) {
        await updateConferenceQuestion(conferenceId, editing.id, payload, companyId);
        setEditorOpen(false);
        setEditing(null);
        await refresh();
      } else {
        await createConferenceQuestion(conferenceId, payload, companyId);
        setEditorOpen(false);
        setSavedToInventory(true);
        await refresh();
      }
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Save failed');
    }
  }

  if (!hasScope) {
    return (
      <Empty
        description={selectTenantPrompt("manage this conference's Discovery questions")}
        style={{ padding: '48px 0' }}
      />
    );
  }

  const showEmpty = !loading && questions.length === 0 && !startedBlank;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        message="These are copies — editing here won't change the Inventory or any other conference's set. Use ↑ ↓ to set the order reps see."
      />

      {showEmpty ? (
        <Empty description="No questions yet for this conference">
          {canManage && (
            <Space>
              <Button type="primary" onClick={seedDefaults}>Seed Inventory defaults</Button>
              <Button onClick={() => setStartedBlank(true)}>Start blank</Button>
            </Space>
          )}
        </Empty>
      ) : (
        <>
          {canManage && (
            <Space>
              <Button onClick={openAddModal}>+ Add from Inventory</Button>
              <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>+ New question</Button>
            </Space>
          )}

          {questions.map((q, i) => (
            <QuestionCard
              key={q.id}
              q={q}
              index={i}
              count={questions.length}
              onMove={(dir) => onMove(i, dir)}
              onEdit={() => { setEditing(q); setEditorOpen(true); }}
              onRemove={() => onRemove(q.id)}
              canManage={canManage}
            />
          ))}

          {savedToInventory && (
            <Alert
              type="success"
              closable
              onClose={() => setSavedToInventory(false)}
              message='Creating a "New question" here also saved a copy to the Questions Inventory. "Add from Inventory" pulls existing templates in without duplicating them.'
            />
          )}
        </>
      )}

      <Modal
        title="Add from Inventory"
        open={addOpen}
        onOk={confirmAdd}
        onCancel={() => setAddOpen(false)}
        okText="Add selected"
      >
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Pick questions to copy in"
          value={picked}
          onChange={setPicked}
          options={inventory.map((q) => ({ label: q.text, value: q.id }))}
          optionFilterProp="label"
        />
      </Modal>

      <Modal
        title={editing ? 'Edit question' : 'New question'}
        open={editorOpen}
        footer={null}
        onCancel={() => { setEditorOpen(false); setEditing(null); }}
        destroyOnClose
      >
        <QuestionForm
          initial={editing as unknown as DiscoveryQuestion | null}
          showDefault={false}
          onSubmit={onSubmitEditor}
          onCancel={() => { setEditorOpen(false); setEditing(null); }}
        />
      </Modal>
    </Space>
  );
}
